/**
 * MUI Carousel component that replaces the styled-components Carousel
 * 
 * This component provides a Material UI equivalent of the original Carousel component
 * with similar props and functionality.
 */
import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { Box, IconButton, styled, Theme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * Interface for Carousel props
 */
interface CarouselProps {
  children?: ReactNode;
  activeIndex?: number;
  onChange?: (index: number) => void;
  autoPlay?: boolean;
  interval?: number;
  className?: string;
  [key: string]: any; // For additional props
}

/**
 * Interface for styled component props
 */
interface CarouselItemProps {
  active: boolean;
  theme?: Theme;
}

/**
 * MUI Carousel component
 * 
 * @param props - Component props
 * @returns MUI Carousel component
 */
const MuiCarousel: React.FC<CarouselProps> = (props) => {
  const { 
    children, 
    activeIndex = 0, 
    onChange, 
    autoPlay = false, 
    interval = 5000,
    ...otherProps 
  } = props;
  
  // Convert children to array for easier manipulation
  const childrenArray = React.Children.toArray(children || []);
  const childCount = childrenArray.length;
  
  const [currentIndex, setCurrentIndex] = useState<number>(activeIndex);
  // Use any type for now to avoid TypeScript complexities with setTimeout
  const autoPlayRef = useRef<any>(null);
  
  // Handle auto-play
  useEffect(() => {
    if (!autoPlay) return;
    
    // Using vanilla setTimeout
    const timerId = setTimeout(() => {
      handleNext();
    }, interval);
    
    // Store the timer ID
    autoPlayRef.current = timerId;
    
    return () => {
      // Clean up on unmount or when dependencies change
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, [currentIndex, autoPlay, interval]);
  
  // Update current index when activeIndex prop changes
  useEffect(() => {
    setCurrentIndex(activeIndex);
  }, [activeIndex]);
  
  const handlePrev = (): void => {
    const newIndex = currentIndex === 0 ? (childCount - 1) : (currentIndex - 1);
    setCurrentIndex(newIndex);
    if (onChange) onChange(newIndex);
  };
  
  const handleNext = (): void => {
    const newIndex = currentIndex === (childCount - 1) ? 0 : (currentIndex + 1);
    setCurrentIndex(newIndex);
    if (onChange) onChange(newIndex);
  };
  
  const handleDotClick = (index: number): void => {
    setCurrentIndex(index);
    if (onChange) onChange(index);
  };
  
  return (
    <CarouselContainer {...otherProps}>
      <CarouselContent>
        <IconButton 
          onClick={handlePrev} 
          className="carousel-control prev"
          aria-label="Previous slide"
        >
          <ArrowBackIcon />
        </IconButton>
        
        <CarouselItems>
          {React.Children.map(children, (child, index) => (
            <CarouselItem active={index === currentIndex}>
              {child}
            </CarouselItem>
          ))}
        </CarouselItems>
        
        <IconButton 
          onClick={handleNext} 
          className="carousel-control next"
          aria-label="Next slide"
        >
          <ArrowForwardIcon />
        </IconButton>
      </CarouselContent>
      
      <CarouselDots>
        {React.Children.map(children, (_, index) => (
          <CarouselDot 
            active={index === currentIndex} 
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </CarouselDots>
    </CarouselContainer>
  );
};

// Styled components using MUI styled API
const CarouselContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
}));

const CarouselContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  
  '& .carousel-control': {
    zIndex: 2,
    color: 'var(--app-dark-gray)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
  },
}));

const CarouselItems = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
}));

const CarouselItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<CarouselItemProps>(({ active, theme }) => ({
  width: '100%',
  flexShrink: 0,
  display: active ? 'block' : 'none',
  transition: 'opacity 0.5s ease-in-out',
  opacity: active ? 1 : 0,
}));

const CarouselDots = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(1),
  gap: theme.spacing(1),
}));

const CarouselDot = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<CarouselItemProps>(({ active, theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: 'none',
  padding: 0,
  backgroundColor: active ? 'var(--app-red)' : 'var(--app-light-gray)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: active ? 'var(--app-red)' : 'var(--app-gray)',
  },
}));

export default MuiCarousel;
